import React, { useEffect, useState } from 'react'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Autoplay } from 'swiper/modules';
import HomeVideo2 from '../../../assets/images/banner/banner-1.mp4';
import FooterContact from '../../../component/FooterContact';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
};

const features = [
    { icon: '⚡', title: 'Enhanced Efficiency', desc: 'Automate repetitive tasks so your team can focus on high-priority threats, reducing manual efforts and human error.' },
    { icon: '🚀', title: 'Faster Incident Response', desc: 'Detect, triage, and respond to incidents faster with automated actions that enable swift containment and remediation.' },
    { icon: '🔍', title: 'Proactive Threat Hunting', desc: 'Shift from reactive to proactive security by continuously monitoring for potential risks before they escalate.' },
    { icon: '📈', title: 'Scalable Solution', desc: 'Scales seamlessly from small businesses to large enterprises as threats and your organization evolve.' },
    { icon: '🤝', title: 'Improved Collaboration', desc: 'Unified platform for communication, investigation, and decision-making across all security teams.' },
    { icon: '📋', title: 'Compliance & Reporting', desc: 'Automatically generate detailed reports and audit trails to meet regulatory requirements effortlessly.' }
];

const benefits = [
    'Build workflows and streamline operations',
    'Increase flexibility, extensibility, and collaboration',
    'Respond more quickly and accurately',
    'Improve analyst job satisfaction',
    'Improve time management and productivity',
    'Effectively manage incidents',
    'Automate repeated and error-prone tasks',
    'Simplify collaboration across teams'
];

const testimonials = [
    { quote: "CLOUD MIND has been a game changer for our cybersecurity strategy. Their comprehensive protection across our email, cloud, and network systems has kept our data secure from evolving threats.", author: "IT Director at Manufacturing" },
    { quote: "We turned to CLOUD MIND for a complete overhaul of our cybersecurity protocols, and it was the best decision we made. Their expert team implemented robust security measures.", author: "Chief Information Security Officer" },
    { quote: "CLOUD MIND's email security and network defense solutions have been critical in preventing cyber threats. We've seen a significant reduction in phishing attempts and other security breaches.", author: "Operations Manager at Agriculture" },
    { quote: "Since partnering with CLOUD MIND, our network and cloud systems have been fortified with state-of-the-art security protocols, ensuring compliance and protecting our clients' data.", author: "Founder at Healthcare" },
    { quote: "CLOUD MIND has delivered unmatched expertise in securing our cloud services and networks. Their end-to-end solutions have helped us prevent data breaches and avoid costly downtime.", author: "CTO of Real Estate" }
];

export default function SOAR() {

    const [activeFeature, setActiveFeature] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            {/* Hero Banner */}
            <section className="tech-hero-banner" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="tech-hero-overlay"></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center" style={{ minHeight: '85vh' }}>
                        <div className="col-lg-7 col-md-12">
                            <motion.div
                                className="tech-hero-content"
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                            >
                                <motion.div variants={fadeUp} className="tech-breadcrumb">
                                    <Link to="/">Home</Link>
                                    <span className="tech-breadcrumb-sep"> / </span>
                                    <span>SOAR</span>
                                </motion.div>
                                <motion.div variants={fadeUp} className="tech-badge">
                                    Security Automation Platform
                                </motion.div>
                                <motion.h1 variants={fadeUp} className="tech-hero-title">
                                    Security Orchestration<br />
                                    <span className="tech-hero-gradient">Automation & Response</span>
                                </motion.h1>
                                <motion.p variants={fadeUp} className="tech-hero-desc">
                                    By 2023, 75% of organizations restructured risk and security governance. A resilient SOAR strategy is essential to protecting against threats while keeping your business running.
                                </motion.p>
                                <motion.div variants={fadeUp} className="tech-hero-actions">
                                    <Link to="/contact" className="tech-btn-primary">Get Started Today</Link>
                                    <Link to="/contact" className="tech-btn-ghost">Schedule Demo</Link>
                                </motion.div>
                            </motion.div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <motion.div
                                className="tech-hero-visual"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                            >
                                <div className="soar-visual">
                                    <div className="soar-center-node">SOAR</div>
                                    {['Orchestrate', 'Automate', 'Respond', 'Analyze'].map((label, i) => (
                                        <div key={i} className={`soar-orbit-node soar-node-${i + 1}`}>
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                    <svg className="soar-connections" viewBox="0 0 200 200">
                                        <circle cx="100" cy="100" r="70" stroke="rgba(36,88,243,0.3)" strokeWidth="1" fill="none" strokeDasharray="5,5" />
                                        <circle cx="100" cy="100" r="45" stroke="rgba(36,88,243,0.2)" strokeWidth="1" fill="none" />
                                    </svg>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <video autoPlay loop muted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo2} type="video/mp4" />
                </video>
            </section>

            {/* How SOAR Works */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/soar-2.webp')} alt='SOAR' />
                                <div className="tech-image-badge">
                                    <span className="badge-num">75%</span>
                                    <span className="badge-label">Faster Response</span>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">Platform Overview</div>
                            <h2 className="tech-section-heading">How <span className="tech-text-accent">SOAR Works</span></h2>
                            <p className="tech-section-para">
                                Security orchestration, automation, and response (SOAR) primarily focuses on threat management, security operations automation, and security incident responses. SOAR platforms can instantly assess, detect, intervene, or search through incidents without constant human interaction.
                            </p>
                            <div className="tech-feature-pills">
                                {['Threat Detection', 'Auto-Triage', 'Incident Response', 'Reporting'].map((pill, i) => (
                                    <span key={i} className="tech-pill">{pill}</span>
                                ))}
                            </div>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Talk To Our Experts</Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* SOAR vs SIEM */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">Comparison</div>
                        <h2 className="tech-section-heading text-white">SOAR vs <span className="tech-text-accent">SIEM</span></h2>
                    </motion.div>
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-compare-card">
                                <div className="tech-compare-header">
                                    <span className="tech-compare-icon">📊</span>
                                    <h3>SIEM</h3>
                                </div>
                                <p>Collects, analyzes, and stores security-related data including incidents and events. Requires calibration and expert oversight to determine data accuracy - can be labor intensive.</p>
                                <ul className="tech-compare-list">
                                    <li>Collects & stores log data</li>
                                    <li>Requires human analysis</li>
                                    <li>Generates security alerts</li>
                                    <li>Manual triage process</li>
                                </ul>
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-compare-card accent">
                                <div className="tech-compare-header">
                                    <span className="tech-compare-icon">⚡</span>
                                    <h3>SOAR</h3>
                                </div>
                                <p>Automated programs that typically do not require expert human oversight to determine if security events are false positives. Time spent investigating is used much more efficiently.</p>
                                <ul className="tech-compare-list">
                                    <li>Automated threat response</li>
                                    <li>Minimal human oversight needed</li>
                                    <li>Orchestrates security tools</li>
                                    <li>Faster incident remediation</li>
                                </ul>
                            </div>
                        </motion.div>
                    </div>
                    <motion.div
                        className="tech-combined-note mt-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <h3 className="text-white">Best Together: SIEM + SOAR</h3>
                                <p className="text-white" style={{ opacity: 0.85 }}>Large organizations receive up to millions of alerts daily. SIEM gathers and analyzes them, while SOAR processes and manages incident response much faster - removing time-consuming manual prioritization.</p>
                            </div>
                            <div className="col-lg-4 text-center text-lg-end">
                                <img src={require('../../../assets/images/services/technology/siem.webp')} alt='SIEM SOAR' style={{ maxWidth: 200, borderRadius: 12 }} />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 6 Features Grid */}
            <section className="tech-section-light">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag">Why Choose Us</div>
                        <h2 className="tech-section-heading">Why Choose <span className="tech-text-accent">CLOUD MIND SOAR?</span></h2>
                        <p className="tech-section-para" style={{ maxWidth: 600, margin: '0 auto' }}>
                            Our SOAR solution keeps you one step ahead of attackers while improving your overall security operations.
                        </p>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {features.map((feat, i) => (
                            <motion.div className="col-lg-4 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div
                                    className={`tech-feature-card ${activeFeature === i ? 'active' : ''}`}
                                    onMouseEnter={() => setActiveFeature(i)}
                                >
                                    <div className="tech-feature-icon">{feat.icon}</div>
                                    <h3 className="tech-feature-title">{feat.title}</h3>
                                    <p className="tech-feature-desc">{feat.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Benefits */}
            <section className="tech-section-gradient">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag light">Key Advantages</div>
                            <h2 className="tech-section-heading text-white">SOAR <span className="tech-text-accent">Benefits</span></h2>
                            <ul className="tech-benefits-list">
                                {benefits.map((benefit, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.08 }}
                                    >
                                        <span className="tech-check-icon">✓</span>
                                        {benefit}
                                    </motion.li>
                                ))}
                            </ul>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Get Started</Link>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/soar-benifit.webp')} alt='SOAR Benefits' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">Client Stories</div>
                        <h2 className="tech-section-heading text-white">What Our <span className="tech-text-accent">Clients Say</span></h2>
                    </motion.div>
                    <Swiper
                        spaceBetween={24}
                        breakpoints={{
                            640: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 2 }
                        }}
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        pagination={{ clickable: true }}
                        modules={[Pagination, Autoplay]}
                        className="tech-testimonial-swiper"
                    >
                        {testimonials.map((t, i) => (
                            <SwiperSlide key={i}>
                                <div className="tech-testimonial-card">
                                    <div className="tech-testimonial-quote">"</div>
                                    <p className="tech-testimonial-text">{t.quote}</p>
                                    <div className="tech-testimonial-author">
                                        <div className="tech-testimonial-avatar">{t.author[0]}</div>
                                        <span>{t.author}</span>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            <FooterContact />
        </>
    )
}
